// VersusScene: one round of an online duel. Built like GameScene (same systems and
// update order) on a symmetric arena, plus:
//   • the RIVAL (opponent's mech, network-driven, hittable — damage is shooter-authoritative);
//   • mirrored rival shots (cosmetic ghost projectiles);
//   • surprise drops (server-timed weapons / health / items, first claim wins);
//   • round flow: countdown → fight → round result; first to 3 wins (server keeps score).
// The scene restarts for every round; the connection lives in net/versus.

import Phaser from 'phaser';
import { audio } from '../audio';
import { DEPTH, FONT, MAX_DT, PLAYER_BODY, SCENES, TILE } from '../config/constants';
import { COLORS, css } from '../config/palette';
import type { GameContext, ProjectileSpec, SystemApi, TimeApi } from '../core/contracts';
import { bus, Subscriptions } from '../core/events';
import { clearRun, startRun } from '../core/run';
import { save } from '../core/save';
import { session } from '../core/session';
import type { RouteNode } from '../core/types';
import { ARENAS } from '../data/arenas';
import { SECTORS } from '../data/sectors';
import { versus, type DropMsg, type HitMsg, type RoundMsg, type StateMsg } from '../net/versus';
import { createAbilities } from '../systems/abilities';
import { createBosses } from '../systems/bosses';
import { createCombat } from '../systems/combat';
import { createEnemies } from '../systems/enemies';
import { createFx } from '../systems/fx';
import { createLoot } from '../systems/loot';
import { createPlayer } from '../systems/player';
import type { MechView } from '../systems/player/MechView';
import { createInput } from '../systems/player/input';
import { VersusDrops } from '../systems/versus/drops';
import { BotPilot } from '../systems/versus/bot';
import { RivalMech } from '../systems/versus/rival';
import { createWorld } from '../systems/world';
import { createInteract } from '../systems/world/interact';
import { parseRoom } from '../systems/world/template';

export interface VersusSceneData {
  round: RoundMsg;
}

/** Test hook (as GameScene): ?maxdt=0.25 lets slow headless renderers simulate at real speed. */
const STEP_CAP = (() => {
  const v = Number(new URLSearchParams(window.location.search).get('maxdt'));
  return Number.isFinite(v) && v > 0 ? Math.min(v, 0.5) : MAX_DT;
})();

/** FX the local mech produces that the opponent should see (everything world-space and cosmetic). */
const MIRRORED_FX = ['muzzleFlash', 'sparks', 'impact', 'explosion', 'lightning', 'beam', 'ring', 'smoke', 'embers', 'debris', 'vent', 'afterimage', 'trailSegment'];

/** JSON-safe copy of FX arguments with numbers rounded (smaller messages). */
function roundArgs(a: unknown[]): unknown[] {
  const r = (v: unknown): unknown => {
    if (typeof v === 'number') return Math.round(v * 100) / 100;
    if (Array.isArray(v)) return v.map(r);
    if (v && typeof v === 'object') {
      const o: Record<string, unknown> = {};
      for (const [k, x] of Object.entries(v)) if (typeof x !== 'function') o[k] = r(x);
      return o;
    }
    return v;
  };
  return a.map(r);
}

/** State snapshots per second. */
const SEND_HZ = 20;
/** Post-hit flicker in versus (s): short, so fast weapons still land (dash i-frames still block). */
const VERSUS_HIT_IFRAMES = 0.08;
const WIN_ROUNDS = 3;

/** Spec fields a mirrored shot needs (all JSON-safe). */
const SHOT_KEYS = ['x', 'y', 'angle', 'speed', 'texture', 'frame', 'scale', 'tint', 'glow', 'radius', 'lifetime', 'gravity', 'homing', 'explode', 'field', 'bounces', 'trail', 'weaponId', 'launchSpeedFrac'] as const;

export class VersusScene extends Phaser.Scene {
  private ctx: GameContext | null = null;
  private systems: SystemApi[] = [];
  private subs = new Subscriptions();
  private netOff: (() => void)[] = [];
  private rival: RivalMech | null = null;
  /** VS AI: the local pilot driving the rival. */
  private bot: BotPilot | null = null;
  private drops: VersusDrops | null = null;
  private round!: RoundMsg;
  private clockNow = 0;
  private sendT = 0;
  private equipT = 0;
  private outShots: Partial<ProjectileSpec>[] = [];
  /** Own-mech FX calls this frame (sent as one 'fx' message) and the capture switch. */
  private outFx: unknown[][] = [];
  private capturing = false;
  /** Unwrapped FX methods (used to replay the opponent's effects without re-capturing them). */
  private fxRaw: Record<string, (...a: unknown[]) => unknown> = {};
  private outHits: Omit<HitMsg, 't'>[] = [];
  private fightAt = 0;
  private fighting = false;
  private roundOver = false;
  private sentDead = false;
  private forfeitArmedUntil = 0;
  // HUD
  private hud!: Phaser.GameObjects.Container;
  private center!: Phaser.GameObjects.Text;
  private centerSub!: Phaser.GameObjects.Text;
  private scoreG!: Phaser.GameObjects.Graphics;
  private rivalBar!: Phaser.GameObjects.Graphics;
  private rivalName!: Phaser.GameObjects.Text;
  private hint!: Phaser.GameObjects.Text;

  constructor() {
    super(SCENES.VERSUS);
  }

  create(data: VersusSceneData): void {
    this.round = data.round;
    const side = versus.side;
    const arena = ARENAS[this.round.arena % ARENAS.length];
    const sector = SECTORS[this.round.sector % SECTORS.length];
    this.clockNow = 0;
    this.sendT = 0;
    this.equipT = 0;
    this.outShots = [];
    this.outHits = [];
    this.fighting = false;
    this.roundOver = false;
    this.sentDead = false;
    // Real time, measured from when the round message arrived: both clients start together
    // no matter how long their scene took to build.
    this.fightAt = (versus.roundAt || performance.now()) + this.round.startsIn;

    // A fresh loadout every round: the mech's starting kit.
    const run = startRun({ mechId: versus.mech, seed: (this.round.seed ^ (side ? 0x5bd1e995 : 0)) >>> 0 });
    const node: RouteNode = { id: 'versus', type: 'start', depth: 0, lane: 0, next: [], roomId: arena.id, difficulty: 0, visited: true };

    this.physics.world.resume();
    this.physics.world.timeScale = 1;
    const clockRef = this;
    const time: TimeApi = {
      get now() {
        return clockRef.clockNow;
      },
      hitstop() {
        // No hitstop in versus: both clients must keep simulating.
      },
      get inHitstop() {
        return false;
      },
      slowMo() {
        // No slow motion in versus.
      },
      get scale() {
        return 1;
      },
    };
    const input = createInput(this);
    const ctx = {
      scene: this,
      bus,
      run,
      rng: run.rng.combat,
      get settings() {
        return save.settings;
      },
      audio,
      time,
      input,
    } as unknown as GameContext;
    this.ctx = ctx;

    ctx.fx = createFx(ctx);
    ctx.world = createWorld(ctx, node, sector, { template: arena, spawnIndex: side, versus: true });
    ctx.interact = createInteract(ctx);
    const combat = createCombat(ctx);
    ctx.projectiles = combat.projectiles;
    ctx.damage = combat.damage;
    ctx.status = combat.status;
    ctx.zones = combat.zones;
    ctx.effects = combat.effects;
    ctx.enemies = createEnemies(ctx);
    ctx.bosses = createBosses(ctx);
    ctx.loot = createLoot(ctx);
    ctx.player = createPlayer(ctx, ctx.world.playerSpawn);
    ctx.abilities = createAbilities(ctx);
    this.systems = [input, ctx.world, ctx.player, ctx.abilities, ctx.enemies, ctx.bosses, ctx.projectiles, ctx.zones, ctx.status, ctx.effects, ctx.damage, ctx.loot, ctx.interact, ctx.fx];
    session.ctx = ctx;
    for (const s of this.systems) s.start?.();

    // Mirror every local player shot to the rival (one message per frame).
    const proj = ctx.projectiles;
    const spawn = proj.spawn.bind(proj);
    proj.spawn = (spec: ProjectileSpec) => {
      const h = spawn(spec);
      if (spec.owner === 'player' && !spec.ghost) {
        const s: Record<string, unknown> = {};
        for (const k of SHOT_KEYS) if ((spec as unknown as Record<string, unknown>)[k] !== undefined) s[k] = (spec as unknown as Record<string, unknown>)[k];
        this.outShots.push(s as Partial<ProjectileSpec>);
      }
      return h;
    };

    this.wrapFx(ctx);

    // The rival stands on the other spawn marker until its first snapshot arrives.
    const layout = parseRoom(arena);
    const other = layout.markers.P[side === 0 ? layout.markers.P.length - 1 : 0];
    const opp = this.round.players[1 - side] ?? { name: versus.opponent?.name ?? 'RIVAL', mech: versus.opponent?.mech ?? 'volt' };
    const ai = versus.mode === 'ai';
    this.rival = new RivalMech(ctx, opp.mech, opp.name, other.cx * TILE, (other.ty + 1) * TILE, (h) => {
      const { t: _t, ...rest } = h;
      // VS AI: hits land on the local pilot; online they go to the opponent.
      if (this.bot) this.bot.takeHit(rest);
      else this.outHits.push(rest);
    });
    ctx.enemies.register(this.rival);
    if (ai) {
      this.rival.exact = true;
      this.bot = new BotPilot(ctx, this.rival, opp.mech, other.cx * TILE, (other.ty + 1) * TILE, versus.aiLevel, (m) => versus.fromBot(m), this.round.round);
      // Bot shots use the normal enemy damage path; keep the short versus hit flicker.
      this.subs.on('player:damaged', () => {
        const pl = ctx.player;
        if (pl.alive && !pl.isDashing()) pl.setInvulnerable(VERSUS_HIT_IFRAMES * 1000);
      });
    }
    this.drops = new VersusDrops(ctx, (id) => versus.send({ t: 'claim', id }));
    // Drops that arrived while this scene was still loading.
    for (const d of versus.activeDrops.values()) this.drops.spawn(d);

    // Frozen until the countdown ends.
    ctx.player.setControlEnabled(false);

    const cam = this.cameras.main;
    cam.setBounds(0, 0, ctx.world.width, ctx.world.height);
    cam.setBackgroundColor(sector.palette.skyBottom);
    cam.startFollow(ctx.player.bodyObject, false, 0.14, 0.14);
    cam.centerOn(ctx.player.x, ctx.player.y);
    cam.fadeIn(260, 5, 7, 11);

    if (!this.scene.isActive(SCENES.UI)) this.scene.launch(SCENES.UI);
    this.scene.bringToTop(SCENES.UI);

    this.buildHud();
    this.bindNet();
    this.subs.on('player:died', () => this.reportDeath());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    audio.music(sector.music, { fadeMs: 600 });
  }

  // ───────────────────────────── Network ─────────────────────────────

  private bindNet(): void {
    const on = (t: string, fn: (m: Record<string, unknown>) => void) => this.netOff.push(versus.on(t, fn as never));
    // Messages still in flight from the previous round (e.g. the loser's 'dead' snapshots)
    // must not leak into this one: they used to leave the rival stuck in its death pose.
    const thisRound = (m: Record<string, unknown>) => m.r === undefined || m.r === this.round.round;
    on('state', (m) => thisRound(m) && this.rival?.apply(m as unknown as StateMsg));
    on('shot', (m) => thisRound(m) && this.mirrorShots(m.s as Partial<ProjectileSpec>[]));
    on('hit', (m) => thisRound(m) && this.applyHits(m.h as Omit<HitMsg, 't'>[]));
    on('drop', (m) => this.drops?.spawn(m as unknown as DropMsg));
    on('taken', (m) => {
      const got = this.drops?.taken(Number(m.id), m.by === versus.side);
      if (got && this.bot && m.by !== versus.side) this.bot.grant(got.kind, got.weapon);
    });
    on('round_end', (m) => this.onRoundEnd(Number(m.winner), Boolean(m.over)));
    on('round', (m) => this.time.delayedCall(50, () => this.scene.restart({ round: m as unknown as RoundMsg })));
    on('fx', (m) => thisRound(m) && this.replayFx(m.f as unknown[][]));
    on('opponent_left', () => this.endMatch('OPPONENT LEFT', 'VICTORY BY FORFEIT', COLORS.uiGood));
    on('disconnected', () => this.endMatch('CONNECTION LOST', 'THE MATCH ENDED', COLORS.uiBad));
  }

  /**
   * Record visual effects the local mech makes (dash sparks, afterimages, jump rings, muzzle
   * flashes, ability lightning / explosions / beams…) so the opponent sees them too.
   * Screen-wide effects (shake, flash) and UI (damage numbers, telegraphs) stay local.
   */
  private wrapFx(ctx: GameContext): void {
    const fx = ctx.fx as unknown as Record<string, (...a: unknown[]) => unknown>;
    const player = ctx.player;
    for (const name of MIRRORED_FX) {
      const fn = fx[name];
      if (typeof fn !== 'function') continue;
      const raw = fn.bind(fx);
      this.fxRaw[name] = raw;
      fx[name] = (...args: unknown[]) => {
        if (this.capturing && this.outFx.length < 60) {
          // The only object argument that matters is our own mech (afterimages): send a marker.
          const a = args.map((v) => (v && typeof v === 'object' && (v === player.view.root || v === player.bodyObject) ? '@self' : v));
          if (!a.some((v) => v && typeof v === 'object' && !Array.isArray(v) && Object.values(v as object).some((x) => x && typeof x === 'object' && !Array.isArray(x) && !('x' in (x as object))))) {
            this.outFx.push([name, ...roundArgs(a)]);
          }
        }
        return raw(...args);
      };
    }
  }

  /** Replay the opponent's own-mech effects (afterimages are drawn on the rival's mech). */
  private replayFx(list: unknown[][]): void {
    if (!Array.isArray(list) || !this.rival) return;
    for (const call of list.slice(0, 60)) {
      if (!Array.isArray(call)) continue;
      const [name, ...args] = call as [string, ...unknown[]];
      const fn = this.fxRaw[name];
      if (!fn || !MIRRORED_FX.includes(name)) continue;
      const a = args.map((v) => (v === '@self' ? this.rival!.view.root : v));
      if (a.some((v) => v === '@self')) continue;
      try {
        fn(...a);
      } catch {
        // A malformed call is just a missing effect.
      }
    }
  }

  private mirrorShots(list: Partial<ProjectileSpec>[]): void {
    const ctx = this.ctx;
    if (!ctx || !Array.isArray(list)) return;
    this.rival?.recoil();
    for (const s of list.slice(0, 24)) {
      if (typeof s.x !== 'number' || typeof s.y !== 'number' || typeof s.angle !== 'number') continue;
      ctx.projectiles.spawn({
        ...(s as ProjectileSpec),
        owner: 'enemy',
        ghost: true,
        damage: 0,
        tags: [],
        destructible: false,
        texture: s.texture ?? '',
        radius: s.radius ?? 4,
        lifetime: Math.min(4, s.lifetime ?? 1),
        speed: s.speed ?? 1200,
      });
    }
  }

  /** Hits from the rival's shots: one batch per frame → one hit (pellets add up). */
  private applyHits(list: Omit<HitMsg, 't'>[]): void {
    const ctx = this.ctx;
    if (!ctx || !Array.isArray(list) || !this.fighting) return;
    const p = ctx.player;
    if (!p.alive) return;
    let direct = 0;
    let dot = 0;
    let kb = 0;
    let sx: number | undefined;
    let sy: number | undefined;
    const tags = new Set<string>();
    for (const h of list) {
      const a = Math.max(0, Math.min(400, Number(h.a) || 0));
      if (h.dot) dot += a;
      else {
        direct += a;
        kb = Math.max(kb, Number(h.kb) || 0);
        if (h.sx !== undefined) sx = h.sx;
        if (h.sy !== undefined) sy = h.sy;
        for (const t of h.tags ?? []) tags.add(t);
      }
    }
    // Damage-over-time ticks bypass the hit flicker (they'd otherwise grant i-frames).
    if (dot > 0 && !p.invulnerable) {
      const fromShield = Math.min(p.shield, dot);
      p.shield -= fromShield;
      p.hp = Math.max(0, p.hp - (dot - fromShield));
      if (p.hp <= 0) p.kill();
    }
    if (direct > 0 && !p.invulnerable) {
      ctx.damage.hitPlayer({
        amount: direct,
        tags: [...tags] as never,
        source: 'enemy',
        sourceX: sx ?? this.rival?.x,
        sourceY: sy ?? this.rival?.y,
        knockback: Math.min(700, kb),
        label: 'rival',
        ignoreIFrames: true,
      });
      if (p.alive) p.setInvulnerable(VERSUS_HIT_IFRAMES * 1000);
    }
  }

  private sendState(): void {
    const ctx = this.ctx!;
    const p = ctx.player;
    const v = p.view as unknown as MechView;
    const inv = ctx.run.inventory;
    const msg: StateMsg = {
      t: 'state',
      x: Math.round(p.x * 10) / 10,
      y: Math.round(p.feetY * 10) / 10,
      vx: Math.round(p.vx),
      vy: Math.round(p.vy),
      f: p.facing,
      aim: Math.round(p.aimAngle * 1000) / 1000,
      anim: v.currentAnim,
      fr: v.frameIndex,
      fc: v.frameCount,
      spin: Math.round(v.spinAngle * 1000) / 1000,
      tuck: Math.round(v.tuckAmount * 100) / 100,
      hp: Math.round(p.hp),
      maxHp: Math.round(p.stats.maxHp),
      sh: Math.round(p.shield),
      maxSh: Math.round(p.stats.maxShield),
      alive: p.alive,
      weapon: p.activeWeapon()?.def.id ?? null,
      dash: p.isDashing(),
      r: this.round.round,
    };
    if (this.equipT <= 0) {
      this.equipT = 1;
      try {
        msg.equip = inv.visualIds();
      } catch {
        // optional
      }
    }
    versus.send(msg as never);
  }

  private reportDeath(): void {
    if (this.sentDead || this.roundOver) return;
    this.sentDead = true;
    versus.send({ t: 'dead', round: this.round.round });
  }

  // ───────────────────────────── Round flow ─────────────────────────────

  private onRoundEnd(winner: number, over: boolean): void {
    this.roundOver = true;
    this.fighting = false;
    const won = winner === versus.side;
    this.ctx?.player.setControlEnabled(false);
    this.drawScore();
    if (over) {
      this.endMatch(won ? 'VICTORY' : 'DEFEAT', `${versus.score[versus.side]} - ${versus.score[1 - versus.side]}`, won ? COLORS.uiGood : COLORS.uiBad);
      return;
    }
    this.showCenter(won ? 'ROUND WON' : 'ROUND LOST', 'NEXT ROUND INCOMING', won ? COLORS.uiGood : COLORS.uiBad);
    audio.play(won ? 'ui_confirm' : 'ui_back');
  }

  private endMatch(title: string, sub: string, color: number): void {
    if (this.hint?.active && this.hint.getData('ended')) return;
    this.roundOver = true;
    this.fighting = false;
    this.ctx?.player.setControlEnabled(false);
    versus.leave();
    this.showCenter(title, sub, color);
    this.hint.setText('RETURNING TO MENU…').setData('ended', true);
    // Real time: a hidden / throttled tab still leaves the arena on schedule.
    window.setTimeout(() => {
      if (this.sys.isActive()) this.exitToMenu();
    }, 4200);
  }

  private exitToMenu(): void {
    versus.leave();
    this.scene.stop(SCENES.UI);
    this.scene.start(SCENES.VERSUS_LOBBY);
  }

  // ───────────────────────────── HUD ─────────────────────────────

  private buildHud(): void {
    const W = this.scale.width;
    const t = (y: number, size: number, color: string, font: string = FONT.display) =>
      this.add.text(W / 2, y, '', { fontFamily: font, fontSize: `${size}px`, color, fontStyle: '700', align: 'center' }).setOrigin(0.5).setScrollFactor(0);
    this.hud = this.add.container(0, 0).setScrollFactor(0).setDepth(DEPTH.FX + 50);
    this.scoreG = this.add.graphics().setScrollFactor(0);
    const you = this.add.text(W / 2 - 46, 34, 'YOU', { fontFamily: FONT.display, fontSize: '18px', color: css(COLORS.uiText), fontStyle: '700' }).setOrigin(1, 0.5).setScrollFactor(0);
    const opp = this.round.players[1 - versus.side];
    const them = this.add
      .text(W / 2 + 46, 34, (opp?.name ?? 'RIVAL').toUpperCase(), { fontFamily: FONT.display, fontSize: '18px', color: css(COLORS.threat), fontStyle: '700' })
      .setOrigin(0, 0.5)
      .setScrollFactor(0);
    const roundTxt = this.add
      .text(W / 2, 62, `ROUND ${this.round.round}  ·  FIRST TO ${WIN_ROUNDS}`, { fontFamily: FONT.display, fontSize: '13px', color: css(COLORS.uiDim), fontStyle: '700' })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setLetterSpacing(2);
    this.center = t(this.scale.height * 0.36, 64, '#ffffff');
    this.centerSub = t(this.scale.height * 0.36 + 56, 20, css(COLORS.uiDim));
    this.hint = t(this.scale.height - 150, 15, css(COLORS.uiDim), FONT.mono);
    this.hud.add([this.scoreG, you, them, roundTxt, this.center, this.centerSub, this.hint]);
    this.rivalBar = this.add.graphics().setDepth(DEPTH.PLAYER + 2);
    this.rivalName = this.add
      .text(0, 0, (opp?.name ?? 'RIVAL').toUpperCase(), { fontFamily: FONT.display, fontSize: '13px', color: css(COLORS.threat), fontStyle: '700' })
      .setOrigin(0.5, 1)
      .setDepth(DEPTH.PLAYER + 2);
    this.drawScore();
  }

  private drawScore(): void {
    const W = this.scale.width;
    const g = this.scoreG.clear();
    const me = versus.score[versus.side];
    const them = versus.score[1 - versus.side];
    g.fillStyle(COLORS.uiBg, 0.75).fillRect(W / 2 - 320, 14, 640, 62);
    g.lineStyle(1, COLORS.uiBorder, 1).strokeRect(W / 2 - 320, 14, 640, 62);
    for (let i = 0; i < WIN_ROUNDS; i++) {
      // Round pips sit outside the names: yours on the left, the rival's on the right.
      g.fillStyle(i < me ? COLORS.uiGood : COLORS.steel2, 1).fillCircle(W / 2 - 230 - i * 22, 34, 7);
      g.fillStyle(i < them ? COLORS.threat : COLORS.steel2, 1).fillCircle(W / 2 + 230 + i * 22, 34, 7);
    }
    g.fillStyle(COLORS.uiDim, 1).fillRect(W / 2 - 1, 22, 2, 24);
  }

  private showCenter(title: string, sub: string, color: number): void {
    this.center.setText(title).setColor(css(color)).setAlpha(1).setScale(1.25);
    this.centerSub.setText(sub).setAlpha(1);
    this.tweens.add({ targets: this.center, scale: 1, duration: 260, ease: 'Back.Out' });
  }

  private updateHud(dt: number): void {
    // Countdown → FIGHT.
    if (!this.fighting && !this.roundOver) {
      const left = (this.fightAt - performance.now()) / 1000;
      if (left > 0) {
        const n = Math.ceil(left);
        if (this.center.text !== String(n)) {
          this.showCenter(String(n), `ROUND ${this.round.round}`, COLORS.ultBar);
          audio.play('ui_click', { volume: 0.7 });
        }
      } else {
        this.fighting = true;
        this.ctx!.player.setControlEnabled(true);
        this.showCenter('FIGHT', '', COLORS.threatHot);
        this.tweens.add({ targets: [this.center, this.centerSub], alpha: 0, delay: 600, duration: 400 });
        audio.play('ui_confirm');
      }
    }
    // Rival health bar over its head.
    const r = this.rival;
    const g = this.rivalBar.clear();
    if (r && r.alive) {
      const x = r.x;
      const y = r.y - PLAYER_BODY.h / 2 - 26;
      const w = 64;
      const hpK = Math.max(0, Math.min(1, r.hp / Math.max(1, r.maxHp)));
      const shK = r.maxShield > 0 ? Math.max(0, Math.min(1, r.shield / r.maxShield)) : 0;
      g.fillStyle(COLORS.uiBg, 0.8).fillRect(x - w / 2 - 2, y - 2, w + 4, 10);
      g.fillStyle(COLORS.threat, 1).fillRect(x - w / 2, y, w * hpK, 4);
      g.fillStyle(COLORS.shield, 1).fillRect(x - w / 2, y + 5, w * shK, 2);
      this.rivalName.setPosition(x, y - 4).setVisible(true);
    } else this.rivalName.setVisible(false);
    void dt;
  }

  // ───────────────────────────── Frame ─────────────────────────────

  update(_time: number, deltaMs: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const dt = Math.min(deltaMs / 1000, STEP_CAP);
    ctx.input.update(dt);
    if (ctx.input.pressed('pause') && !this.roundOver) {
      if (this.clockNow < this.forfeitArmedUntil) {
        this.endMatch('FORFEIT', 'YOU LEFT THE MATCH', COLORS.uiBad);
      } else {
        this.forfeitArmedUntil = this.clockNow + 2;
        this.hint.setText('PRESS ESC AGAIN TO FORFEIT');
        this.time.delayedCall(2000, () => {
          if (!this.hint.getData('ended')) this.hint.setText('');
        });
      }
    }
    this.clockNow += dt;
    for (let i = 1; i < this.systems.length; i++) {
      const sys = this.systems[i];
      // The local mech's own visuals (player + abilities) are mirrored to the opponent.
      this.capturing = !this.bot && (sys === ctx.player || sys === ctx.abilities);
      sys.update(dt);
      this.capturing = false;
    }
    this.bot?.update(dt, this.fighting, this.drops?.list() ?? [], (id) => versus.fromBot({ t: 'claim', id }));
    this.rival?.update(dt);
    this.drops?.update(dt);
    this.updateHud(dt);
    // Report the kill the moment the hull hits zero (not after the death animation).
    if (this.fighting && !ctx.player.alive) this.reportDeath();

    // Network: snapshot at SEND_HZ, shots and hits once per frame.
    this.sendT -= dt;
    this.equipT -= dt;
    if (this.sendT <= 0) {
      this.sendT = 1 / SEND_HZ;
      this.sendState();
    }
    if (this.outFx.length) {
      versus.send({ t: 'fx', f: this.outFx.slice(0, 40), r: this.round.round });
      this.outFx = [];
    }
    if (this.outShots.length) {
      versus.send({ t: 'shot', s: this.outShots, r: this.round.round });
      this.outShots = [];
    }
    if (this.outHits.length) {
      if (this.fighting) versus.send({ t: 'hit', h: this.outHits, r: this.round.round });
      this.outHits = [];
    }
    this.updateCamera(dt);
  }

  private updateCamera(dt: number): void {
    const ctx = this.ctx!;
    const p = ctx.player;
    const aim = ctx.input.pointerWorld;
    const tx = Phaser.Math.Clamp((aim.x - p.x) * 0.16 + p.vx * 0.06, -240, 240);
    const ty = Phaser.Math.Clamp((aim.y - p.y) * 0.12, -150, 150);
    const cam = this.cameras.main;
    const k = 1 - Math.exp(-dt * 5);
    const fo = cam.followOffset;
    cam.setFollowOffset(fo.x + (-tx - fo.x) * k, fo.y + (-ty - fo.y) * k);
  }

  private teardown(): void {
    for (const off of this.netOff) off();
    this.netOff = [];
    this.subs.dispose();
    this.drops?.destroy();
    this.drops = null;
    this.bot?.destroy();
    this.bot = null;
    this.rival?.destroy();
    this.rival = null;
    for (const s of [...this.systems].reverse()) {
      try {
        s.destroy();
      } catch (err) {
        console.error('[versus] system destroy failed', err);
      }
    }
    this.systems = [];
    if (session.ctx === this.ctx) session.ctx = null;
    this.ctx = null;
    clearRun();
  }
}
